import React from 'react'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../../state/mint/hooks'
import { Currency, Token, TokenAmount } from '@uniswap/sdk-core'
import { Field } from '../../../state/mint/actions'
import { maxAmountSpend } from '../../../utils/maxAmountSpend'
import { useActiveWeb3React } from '../../../hooks'
import { useWalletModalToggle } from '../../../state/application/hooks'
import { ButtonConfirmed, ButtonLight, ButtonPrimary } from '../../../components/Button'
import { RowBetween } from '../../../components/Row'
import { ApprovalState, useApproveCallback } from '../../../hooks/useApproveCallback'
import { useTransactionAdder } from '../../../state/transactions/hooks'
import { useMasterChief } from '../../../hooks/useContract'
import { parseUnits } from 'ethers/lib/utils'
import { useTokenBalance } from '../../../state/wallet/hooks'
import { calculateGasMargin, getContract } from '../../../utils'
import { Dots } from '../../Pool/styleds'
import { Text } from 'rebass'
import { MASTERCHIEF_ADDRESS } from '@hyperswap/addresses'
import { abi as MasterChief_ABI } from '../../../abis/masterchief.json'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import styled from 'styled-components'
import { useV2Pair } from 'hooks/useV2Pairs'
import { Pair } from '@uniswap/v2-sdk'

interface StakeProps {
  pairToken: Token | undefined
  pId: number
  token0Currency: Currency | undefined | null
  token1Currency: Currency | undefined | null
  setRefresh: () => void
}

export const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const Stake: React.FC<StakeProps> = ({ pairToken, pId, token0Currency, token1Currency, setRefresh }) => {
  const masterChiefContract = useMasterChief()

  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { account, chainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const userPoolBalance = useTokenBalance(account ?? undefined, pairToken ?? undefined)
  const pair = useV2Pair(token0Currency as Currency, token1Currency as Currency)
  const validPair = pair && pair[1]

  // allowance handling
  const [signatureData] = React.useState<{
    v: number
    r: string
    s: string
    deadline: number
  } | null>(null)

  const [loadingAdd, setLoadingAdd] = React.useState<boolean>(false)
  const {
    dependentField,
    currencies,

    currencyBalances,
    parsedAmounts,

    noLiquidity,
  } = useDerivedMintInfo(pairToken ?? undefined, undefined)

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )
  const [approval, approveCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], MASTERCHIEF_ADDRESS)
  const addTransaction = useTransactionAdder()

  async function onAttemptToApprove() {
    const liquidityAmount = parsedAmounts[Field.CURRENCY_A]

    if (!liquidityAmount) throw new Error('missing liquidity amount')
    approveCallback()
  }

  async function onAdd() {
    if (!chainId || !library || !account || !masterChiefContract) return
    const masterChief = getContract(MASTERCHIEF_ADDRESS, MasterChief_ABI, library, account)

    const estimate = masterChief.estimateGas.deposit
    const method = masterChief.deposit

    const args = [`${pId}`, parseUnits(`${parsedAmounts[Field.CURRENCY_A]?.toExact()}`, 18).toString()]

    setLoadingAdd(true)

    await estimate(...args, {})
      .then((estimatedGasLimit: any) => {
        return method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Stake ${token0Currency?.symbol}-${token1Currency?.symbol}`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setRefresh()
          setLoadingAdd(false)
        })
      })
      .catch((error) => {
        setLoadingAdd(false)
        console.error(error)
      })
  }
  const isDisabled =
    loadingAdd === true ||
    formattedAmounts[Field.CURRENCY_A].length === 0 ||
    parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
    (userPoolBalance && parseFloat(formattedAmounts[Field.CURRENCY_A]) > parseFloat(userPoolBalance?.toExact())) ||
    approval !== ApprovalState.APPROVED

  return (
    <ContainerStyled>
      <CurrencyInputPanel
        value={formattedAmounts[Field.CURRENCY_A]}
        onUserInput={onFieldAInput}
        onMax={() => {
          onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
        }}
        showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
        currency={currencies[Field.CURRENCY_A]}
        id={`${pId}-stakingpool`}
        showCommonBases
        smallTokenName
        pair={validPair && (validPair as Pair)}
      />

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
        ) : (
          <RowBetween>
            <ButtonConfirmed
              onClick={onAttemptToApprove}
              confirmed={approval === ApprovalState.APPROVED || signatureData !== null}
              disabled={approval !== ApprovalState.NOT_APPROVED || signatureData !== null}
              mr="0.5rem"
              fontWeight={400}
              fontSize={12}
            >
              {approval === ApprovalState.PENDING ? (
                <Dots>Approving</Dots>
              ) : approval === ApprovalState.APPROVED || signatureData !== null ? (
                'Approved'
              ) : (
                'Approve'
              )}
            </ButtonConfirmed>
            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onAdd()}
              disabled={isDisabled}
            >
              <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {loadingAdd ? 'Staking...' : 'Stake'}
              </Text>
            </ButtonPrimary>
          </RowBetween>
        )}
      </div>
    </ContainerStyled>
  )
}
export default Stake
